import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { toast } from "sonner";
import { api, apiError, get } from "@/lib/api";
import { KpiTile, PageHeader, Section } from "@/components/common/Layout";
import { DataTable } from "@/components/common/DataTable";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { money, title } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

const GROUPS = ["day", "month", "campaign", "language", "country", "provider", "phone_number"];

export default function Costs() {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [groupBy, setGroupBy] = useState("day");
  const [budget, setBudget] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["costs", groupBy],
    queryFn: () => get("/costs", { group_by: groupBy }),
  });

  const saveBudget = useMutation({
    mutationFn: () =>
      api.put("/budgets", {
        scope: "global",
        daily_limit: Number(budget.daily_limit),
        monthly_limit: Number(budget.monthly_limit),
      }),
    onSuccess: () => {
      toast.success("Budget limits updated");
      setBudget(null);
      queryClient.invalidateQueries({ queryKey: ["costs"] });
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const totals = data?.totals || {};
  const daily = data?.daily_usage_pct || 0;
  const monthly = data?.monthly_usage_pct || 0;

  const alertTone = (value) => (value >= 100 ? "danger" : value >= 90 ? "warning" : value >= 70 ? "warning" : "success");

  return (
    <>
      <PageHeader testId="costs-page" title="Costs & usage" subtitle="Per-call cost breakdown, budgets and automatic pause protection" />

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-7">
        <KpiTile testId="cost-kpi-total" label="Total spend" value={money(totals.total)} />
        <KpiTile testId="cost-kpi-calls" label="Billed calls" value={totals.calls ?? 0} />
        <KpiTile testId="cost-kpi-telephony" label="Telephony" value={money(totals.telephony)} />
        <KpiTile testId="cost-kpi-llm" label="LLM" value={money(totals.llm)} />
        <KpiTile testId="cost-kpi-stt" label="STT" value={money(totals.stt)} />
        <KpiTile testId="cost-kpi-tts" label="TTS" value={money(totals.tts)} />
        <KpiTile testId="cost-kpi-storage" label="Recording storage" value={money(totals.storage)} />
      </div>

      {data?.cost_spike ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-800" data-testid="cost-spike-alert">
          Unexpected cost spike on {data.cost_spike.day}: {money(data.cost_spike.amount)} versus a
          {" "}{money(data.cost_spike.average)} recent daily average.
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Section title="Daily budget" testId="daily-budget-panel">
          <div className="space-y-2 p-4">
            <div className="flex items-baseline justify-between">
              <span className="kpi-value">{money(data?.today_spend)}</span>
              <span className="text-sm text-slate-500">of {money(data?.budget?.daily_limit)}</span>
            </div>
            <Progress value={Math.min(daily, 100)} className="h-2" />
            <p className={`text-xs font-medium ${daily >= 90 ? "text-rose-600" : daily >= 70 ? "text-amber-600" : "text-emerald-600"}`}>
              {daily}% used {daily >= 100 ? "· campaigns auto-paused" : daily >= 90 ? "· 90% alert" : daily >= 70 ? "· 70% alert" : ""}
            </p>
          </div>
        </Section>

        <Section title="Monthly budget" testId="monthly-budget-panel">
          <div className="space-y-2 p-4">
            <div className="flex items-baseline justify-between">
              <span className="kpi-value">{money(data?.month_spend)}</span>
              <span className="text-sm text-slate-500">of {money(data?.budget?.monthly_limit)}</span>
            </div>
            <Progress value={Math.min(monthly, 100)} className="h-2" />
            <p className={`text-xs font-medium ${monthly >= 90 ? "text-rose-600" : monthly >= 70 ? "text-amber-600" : "text-emerald-600"}`}>
              {monthly}% used
            </p>
          </div>
        </Section>

        <Section title="Budget limits" testId="budget-form">
          <div className="space-y-3 p-4">
            <div className="space-y-1.5">
              <Label>Daily limit (USD)</Label>
              <Input
                data-testid="budget-daily-input"
                type="number"
                disabled={!can("costs", "write")}
                value={budget?.daily_limit ?? data?.budget?.daily_limit ?? 0}
                onChange={(e) => setBudget((b) => ({ ...(b || data.budget), daily_limit: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Monthly limit (USD)</Label>
              <Input
                data-testid="budget-monthly-input"
                type="number"
                disabled={!can("costs", "write")}
                value={budget?.monthly_limit ?? data?.budget?.monthly_limit ?? 0}
                onChange={(e) => setBudget((b) => ({ ...(b || data.budget), monthly_limit: e.target.value }))}
              />
            </div>
            <Button
              className="w-full bg-blue-600 hover:bg-blue-700"
              disabled={!budget || saveBudget.isPending || !can("costs", "write")}
              onClick={() => saveBudget.mutate()}
              data-testid="budget-save"
            >
              Save budget
            </Button>
          </div>
        </Section>
      </div>

      <Section title="Daily spend trend" testId="cost-trend">
        <div className="h-56 px-2 py-3">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data?.daily_series || []}>
              <defs>
                <linearGradient id="costFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0284C7" stopOpacity={0.25} />
                  <stop offset="100%" stopColor="#0284C7" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#E2E8F0" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748B" }} />
              <YAxis tick={{ fontSize: 11, fill: "#64748B" }} />
              <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }} />
              <Area type="monotone" dataKey="total" stroke="#0284C7" fill="url(#costFill)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </Section>

      <Section
        title="Cost breakdown"
        testId="cost-breakdown"
        actions={
          <Select value={groupBy} onValueChange={setGroupBy}>
            <SelectTrigger data-testid="cost-group-select" className="h-9 w-44"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              {GROUPS.map((g) => <SelectItem key={g} value={g}>By {title(g)}</SelectItem>)}
            </SelectContent>
          </Select>
        }
      >
        <DataTable
          testId="cost-table"
          loading={isLoading}
          columns={[
            { key: "_id", label: title(groupBy), render: (r) => <span className="font-medium text-slate-800">{r._id || "—"}</span> },
            { key: "calls", label: "Calls" },
            { key: "telephony", label: "Telephony", render: (r) => money(r.telephony) },
            { key: "llm", label: "LLM", render: (r) => money(r.llm) },
            { key: "stt", label: "STT", render: (r) => money(r.stt) },
            { key: "tts", label: "TTS", render: (r) => money(r.tts) },
            { key: "storage", label: "Storage", render: (r) => money(r.storage) },
            { key: "total", label: "Total", render: (r) => <span className="font-medium">{money(r.total)}</span> },
          ]}
          rows={(data?.grouped || []).map((r, i) => ({ ...r, id: i }))}
          empty={{ title: "No usage recorded" }}
        />
      </Section>

      <Section title="Campaign budgets" testId="campaign-budgets">
        <DataTable
          testId="campaign-budgets-table"
          columns={[
            { key: "campaign_name", label: "Campaign" },
            { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> },
            { key: "daily_budget", label: "Daily budget", render: (r) => money(r.daily_budget) },
            { key: "total_budget", label: "Total budget", render: (r) => money(r.total_budget) },
            { key: "spent", label: "Spent", render: (r) => money(r.spent) },
            {
              key: "usage_pct",
              label: "Usage",
              render: (r) => (
                <div className="flex w-32 items-center gap-2">
                  <Progress value={Math.min(r.usage_pct, 100)} className="h-1.5" />
                  <span className="font-mono text-xs text-slate-600">{r.usage_pct}%</span>
                </div>
              ),
            },
            {
              key: "alert",
              label: "Alert",
              render: (r) => (
                <StatusBadge
                  value={r.usage_pct >= 100 ? "failed" : r.usage_pct >= 90 ? "paused" : r.usage_pct >= 70 ? "maybe" : "completed"}
                  label={r.usage_pct >= 100 ? "Auto-paused" : r.usage_pct >= 90 ? "90% alert" : r.usage_pct >= 70 ? "70% alert" : "Healthy"}
                />
              ),
            },
          ]}
          rows={(data?.campaign_budgets || []).map((r) => ({ ...r, id: r.campaign_id }))}
          empty={{ title: "No campaign budgets configured" }}
        />
      </Section>
    </>
  );
}
